import {Capacitor} from '@capacitor/core'
import {Checkbox, SelectItem} from '@heroui/react'
import {Icon} from '@iconify/react'
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import {useEffect, useRef, useState} from 'react'
import PButton from '../components/PButton'
import PInput from '../components/PInput'
import PSelect from '../components/PSelect'
import PSelectCountry from '../components/PSelectCountry'
import {ClubType, SportTypes, UserRole} from '../constants'
import {useGlobalModal} from '../core/GlobalModal'
import {useJwtAuth, useJwtUser} from '../JWTAuthProvider'
import {apiAction} from '../api'
import {config} from '../config/config'
import LegalModal from '../user/LegalModal'
import {startGoogleSignIn} from '../utilities/googleAuth'
import {clearGeneratorHandoff, createFromHandoff, needsOrganizerCompletion, readGeneratorHandoff, stashGeneratorTournament} from '../start/generatorHandoff'

// Landing spot after "Make it live" on a free generator page. Logged-out visitors get a two-step
// organizer signup (community details, then account — email/password or Google); once signed in —
// or if already signed in — we replay their stashed teams + format into a real tournament and drop
// them on its Draws page. Google is a redirect round-trip: details are stashed in sessionStorage,
// and the bare socialite-created account is upgraded to organizer on return (see completeGoogle).
export const Route = createFileRoute('/start/from-generator')({
    component: FromGenerator,
    head: () => ({meta: [{title: 'Create your tournament | ScorePal'}, {name: 'robots', content: 'noindex'}]}),
})

// Community details stashed across the Google OAuth round-trip (per-tab, survives redirects).
const GOOGLE_DETAILS_KEY = 'fg_google_details'

function FromGenerator() {
    const user = useJwtUser()
    const jwt = useJwtAuth()
    const navigate = useNavigate()
    const globalModal = useGlobalModal()
    const data = useRef(readGeneratorHandoff())

    const [phase, setPhase] = useState<'loading' | 'signup' | 'creating' | 'error'>('loading')
    const [status, setStatus] = useState('Setting things up…')
    const created = useRef(false)

    // Two-step signup: 1 = community details, 2 = account (Google or email/password)
    const [stepNo, setStepNo] = useState<1 | 2>(1)
    const [sport, setSport] = useState(data.current?.sport || 'padel')
    const [communityName, setCommunityName] = useState('')
    const [countryCode, setCountryCode] = useState('')
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [accepted, setAccepted] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [formError, setFormError] = useState('')

    // Upgrade a bare Google account (EmptyAfterSignup, created by the socialite callback) into an
    // organizer with the community details stashed before the redirect. Falls back to sensible
    // defaults if the stash is missing so the account never stays half-made.
    const completeGoogle = async (u: any) => {
        let details: {sport?: string; communityName?: string; countryCode?: string} = {}
        try { details = JSON.parse(sessionStorage.getItem(GOOGLE_DETAILS_KEY) || '{}') } catch { /* defaults below */ }
        const res: any = await apiAction('signup', {
            type:        'organizer',
            landing_url: jwt.landingUrl,
            platform:    Capacitor.getPlatform(),
            profile:     {email: u.email, password: '', name: u.name || '', lastname: '', inviter_uid: ''},
            questionnaire: {
                sport: details.sport || data.current?.sport || 'padel',
                elo_grade: null, how_long_playing: null, how_often: null, nationality: null,
                club: {
                    type: ClubType.Community,
                    name: (details.communityName || '').trim() || `${(u.name || 'My').trim()}'s Events`,
                    country_code: details.countryCode || '',
                    city: '', city_coordinates: null, meta: {},
                    // No audience question in this funnel: the community default, sent explicitly
                    // so the tournament it builds next is never refused for an unanswered path.
                    organizer_path: 'community',
                },
            },
        })
        if (res?.error) throw new Error(res.error)
        jwt.setFieldsFromResponse(res)
        sessionStorage.removeItem(GOOGLE_DETAILS_KEY)
        // Fire the signup conversion inline (the password path routes through /signup/thank-you/,
        // but another redirect here would orphan the in-flight build).
        const gtag = (window as any).gtag
        if (typeof gtag === 'function' && window.location.hash !== '#no-tracking') {
            gtag('event', 'sign_up', {method: 'google', signup_type: 'organizer'})
            gtag('event', 'conversion', {send_to: 'AW-17042305006/ec_JCKW6_8ocEO7fs74_'})
        }
    }

    useEffect(() => {
        if (!data.current) { navigate({to: '/'}); return }
        const token = typeof localStorage !== 'undefined' ? localStorage.getItem('jwt_token') : null
        if (!token) { setPhase('signup'); return }
        if (!user?.uid) return
        if (created.current) return
        created.current = true
        setPhase('creating')
        ;(async () => {
            try {
                // Returning from Google with a bare socialite account, or an existing player with
                // no club? Finish the organizer signup (role + community) before building, so the
                // account matches the password path.
                if (needsOrganizerCompletion(user)) {
                    setStatus('Finishing your account…')
                    await completeGoogle(user)
                }
                const uid = await createFromHandoff(user.uid, data.current!, setStatus)
                clearGeneratorHandoff()
                window.location.href = `/tournaments/${uid}/draws`
            } catch (e: any) {
                setStatus(e?.message || 'We could not set up your tournament automatically.')
                setPhase('error')
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid])

    const openLegal = (e: any, path: string) => { e.preventDefault(); e.stopPropagation(); globalModal.open(<LegalModal pathname={path} />) }

    const continueToAccount = (e: any) => {
        e.preventDefault()
        if (!communityName.trim() || !countryCode) { setFormError('Please fill in every field.'); return }
        setFormError('')
        setStepNo(2)
    }

    const googleSignup = () => {
        if (!accepted) { setFormError('Please accept the Terms & Conditions to continue.'); return }
        setFormError('')
        // Everything must survive the OAuth round-trip: the community details for the account
        // upgrade, and the chosen sport inside the tournament handoff itself.
        if (data.current) stashGeneratorTournament({...data.current, sport})
        sessionStorage.setItem(GOOGLE_DETAILS_KEY, JSON.stringify({sport, communityName: communityName.trim(), countryCode}))
        startGoogleSignIn('/start/from-generator/')
    }

    const submitSignup = async (e: any) => {
        e.preventDefault()
        if (!name.trim() || !email.trim() || !password) {
            setFormError('Please fill in every field.'); return
        }
        if (!accepted) { setFormError('Please accept the Terms & Conditions to continue.'); return }
        setSubmitting(true); setFormError('')
        // Persist the chosen sport into the handoff so the created tournament uses it.
        if (data.current) stashGeneratorTournament({...data.current, sport})
        try {
            const res: any = await apiAction('signup', {
                type:        'organizer',
                landing_url: jwt.landingUrl,
                platform:    Capacitor.getPlatform(),
                profile:     {email: email.trim(), password, name: name.trim(), lastname: '', inviter_uid: ''},
                questionnaire: {
                    sport,
                    elo_grade: null, how_long_playing: null, how_often: null, nationality: null,
                    // Explicit community, as on the Google leg above: never refused mid-funnel.
                    club: {type: ClubType.Community, name: communityName.trim(), country_code: countryCode, city: '', city_coordinates: null, meta: {}, organizer_path: 'community'},
                },
            })
            if (res?.error) { setFormError(res.error); setSubmitting(false); return }
            jwt.setFieldsFromResponse(res)
            // Route through the tracked thank-you page (fires the signup conversion), then back here —
            // now authenticated — to build the tournament.
            navigate({to: '/signup/thank-you/', search: {next: '/start/from-generator', type: 'organizer'}} as any)
        } catch (err: any) {
            setFormError(err?.message || 'Sign up failed. Please try again.'); setSubmitting(false)
        }
    }

    if (phase === 'signup') {
        const teamCount = data.current?.teams?.length || 0
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F6F7FB] px-5 py-10">
                {stepNo === 1 ? (
                    <form onSubmit={continueToAccount} className="w-full max-w-md bg-white border border-[#e5e7eb] rounded-2xl p-7 shadow-sm">
                        <h1 className="font-bebas text-2xl text-[#1a1a2e] font-bold tracking-wide">You're one step away</h1>
                        <p className="font-inter text-sm text-[#1a1a2e]/70 mt-1">
                            Tell us about your community and we'll build your {teamCount ? `${teamCount}-team ` : ''}tournament from what you just entered — live scores, standings and a shareable page.
                        </p>

                        <div className="mt-5 space-y-4">
                            <PSelect label="Sport" selectedKeys={sport ? [sport] : []}
                                     onSelectionChange={(e: any) => setSport(e.currentKey || 'padel')} isRequired>
                                {Object.entries(SportTypes).map(([key, label]) => (
                                    <SelectItem key={key} textValue={label as string}>{label as string}</SelectItem>
                                ))}
                            </PSelect>

                            <PInput label="Community name" placeholder="e.g. Riverside Padel Club" value={communityName}
                                    onChange={(e) => setCommunityName(e.target.value)} isRequired />

                            <PSelectCountry label="Country" selectedKeys={countryCode ? [countryCode] : []}
                                            onValueChange={(k: any) => setCountryCode(k || '')} />

                            {formError && <p className="text-sm text-danger">{formError}</p>}

                            <PButton type="submit" color="primary" className="w-full">
                                Continue →
                            </PButton>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={submitSignup} className="w-full max-w-md bg-white border border-[#e5e7eb] rounded-2xl p-7 shadow-sm">
                        <h1 className="font-bebas text-2xl text-[#1a1a2e] font-bold tracking-wide">Create your account</h1>
                        <p className="font-inter text-sm text-[#1a1a2e]/70 mt-1">
                            Last step — create your free organizer account and your tournament is built instantly.
                        </p>

                        <div className="mt-5 space-y-4">
                            <Checkbox size="sm" isSelected={accepted} onValueChange={setAccepted}>
                                <span className="text-sm">I agree to ScorePal's{' '}
                                    <a onClick={(e) => openLegal(e, '/terms/')} className="underline text-primary cursor-pointer">T&amp;C's</a>{' '}and{' '}
                                    <a onClick={(e) => openLegal(e, '/privacy/')} className="underline text-primary cursor-pointer">Privacy Policy</a>
                                </span>
                            </Checkbox>

                            {!config.isCordova && <>
                                <PButton type="button" onPress={googleSignup} color="default"
                                         className="w-full border border-[#30322F] bg-white"
                                         startContent={<Icon icon="mdi:google" className="text-xl" />}>
                                    Continue with Google
                                </PButton>

                                <div className="relative text-center text-xs uppercase tracking-wider text-[#1a1a2e]/40">
                                    <span className="relative z-10 bg-white px-3">or</span>
                                    <div className="absolute left-0 right-0 top-1/2 border-t border-[#e5e7eb]" />
                                </div>
                            </>}

                            <PInput label="Your name" placeholder="First and last name" value={name}
                                    onChange={(e) => setName(e.target.value)} isRequired />

                            <PInput label="Email" type="email" placeholder="you@example.com" value={email}
                                    onChange={(e) => setEmail(e.target.value)} isRequired />

                            <PInput label="Password" type="password" placeholder="Create a password" value={password}
                                    onChange={(e) => setPassword(e.target.value)} isRequired />

                            {formError && <p className="text-sm text-danger">{formError}</p>}

                            <PButton type="submit" color="primary" className="w-full" isLoading={submitting}>
                                Create my tournament
                            </PButton>

                            <button type="button" onClick={() => { setFormError(''); setStepNo(1) }}
                                    className="w-full text-center text-sm text-[#1a1a2e]/60 underline cursor-pointer">
                                ← Back to community details
                            </button>
                        </div>
                    </form>
                )}
            </div>
        )
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F6F7FB] px-6">
            <div className="max-w-md w-full bg-white border border-[#e5e7eb] rounded-2xl p-8 text-center">
                {phase !== 'error' ? (
                    <>
                        <div className="mx-auto mb-5 h-10 w-10 rounded-full border-2 border-[#082F8A]/20 border-t-[#082F8A] animate-spin" />
                        <h1 className="font-bebas text-2xl text-[#1a1a2e] font-bold tracking-wide">Building your tournament</h1>
                        <p className="font-inter text-sm text-[#1a1a2e]/70 mt-2">{status}</p>
                        <p className="font-inter text-xs text-[#1a1a2e]/50 mt-4">Keeping your teams and format — no retyping.</p>
                    </>
                ) : (
                    <>
                        <h1 className="font-bebas text-2xl text-[#1a1a2e] font-bold tracking-wide">Almost there</h1>
                        <p className="font-inter text-sm text-[#1a1a2e]/70 mt-2">{status}</p>
                        <button onClick={() => { window.location.href = '/tournaments/' }}
                                className="mt-5 inline-flex items-center justify-center bg-[#082F8A] text-white rounded-full px-6 py-2.5 font-semibold hover:bg-[#06246b] transition">
                            Go to my tournaments
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}
